import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import {
  canPickFolder,
  folders,
  fromInput,
  pickCard,
  topOf,
  type Card,
} from "./card";
import { sha256Of } from "./hash";
import { size } from "./items";
import { createUpload, type Outcome } from "./upload";

interface Progress {
  file: string;
  done: number;
  total: number;
  /** Bytes read to hash plus bytes sent, out of twice the total. */
  bytes: number;
  allBytes: number;
}

interface Finished {
  counts: {
    catalogued: number;
    needs_review: number;
    duplicates: number;
    rejected: number;
  };
  failed: string[];
  unmatched: string[];
}

/**
 * Importing from a card or USB drive in the browser, where the server can't
 * see the computer's drives (design §6.1.1). Each file is hashed as it is
 * read and the server rejects it unless what arrives matches, so two reads
 * of the card agree; the import's manifest records every file.
 */
export function CardImport({ libraryId }: { libraryId: string | null }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [card, setCard] = useState<Card | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [unit, setUnit] = useState("");
  const [progress, setProgress] = useState<Progress | null>(null);
  const [finished, setFinished] = useState<Finished | null>(null);
  const [error, setError] = useState<string | null>(null);
  const running = progress !== null && finished === null;

  useEffect(() => {
    if (!running) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [running]);

  const opened = (c: Card | null) => {
    if (!c) return;
    setCard(c);
    setChosen(new Set(folders(c).keys()));
    setUnit(c.name.replace(/[:\\/]+$/, ""));
    setFinished(null);
    setError(null);
  };

  const run = async () => {
    if (!card || !libraryId) return;
    const files = card.files.filter((f) => chosen.has(topOf(f.path)));
    const allBytes = 2 * files.reduce((n, f) => n + f.file.size, 0);
    let bytes = 0;
    setError(null);
    setProgress({ file: "", done: 0, total: files.length, bytes, allBytes });
    try {
      const batch = await unwrap(
        api.POST("/api/imports", {
          body: {
            library_id: libraryId,
            card: { folder: card.name, label: unit.trim() || null },
          },
        }),
      );
      const entries = [];
      const failed: string[] = [];
      for (const [i, { file, path }] of files.entries()) {
        setProgress({
          file: path,
          done: i,
          total: files.length,
          bytes,
          allBytes,
        });
        try {
          const sha256 = await sha256Of(file, (n) => {
            bytes += n;
            setProgress({
              file: path,
              done: i,
              total: files.length,
              bytes,
              allBytes,
            });
          });
          const before = bytes;
          await new Promise<Outcome | null>((resolve, reject) => {
            createUpload(
              file,
              unit.trim() || undefined,
              batch.id,
              {
                onProgress: (sent) => {
                  bytes = before + sent;
                  setProgress({
                    file: path,
                    done: i,
                    total: files.length,
                    bytes,
                    allBytes,
                  });
                },
                onDone: resolve,
                onError: (m) => reject(new Error(m)),
              },
              undefined,
              sha256,
            ).start();
          });
          bytes = before + file.size;
          entries.push({
            path,
            size: file.size,
            modified: new Date(file.lastModified).toISOString(),
            sha256,
          });
        } catch (e) {
          failed.push(`${path}: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      const check = await unwrap(
        api.POST("/api/imports/{id}/card-manifest", {
          params: { path: { id: batch.id } },
          body: { files: entries },
        }),
      );
      const counts = await unwrap(
        api.POST("/api/imports/{id}/close", {
          params: { path: { id: batch.id } },
        }),
      );
      setFinished({ counts, failed, unmatched: check.unmatched });
      void qc.invalidateQueries({ queryKey: ["imports"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setProgress(null);
    }
  };

  const tops = card ? [...folders(card)] : [];
  const count = card
    ? card.files.filter((f) => chosen.has(topOf(f.path))).length
    : 0;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Card or USB drive"
    >
      <Stack spacing={2}>
        <Typography variant="h6" component="h3">
          From a card or USB drive
        </Typography>
        <Typography color="text.secondary">
          Insert the card, then choose it (or a folder on it). AVGV only reads
          it. Each file is checked twice: as it is read here, and when it
          arrives.
        </Typography>
        <input
          ref={input}
          type="file"
          hidden
          aria-label="Card folder"
          {...{ webkitdirectory: "", directory: "" }}
          onChange={(e) => opened(fromInput(e.target.files))}
        />
        <div>
          <Button
            variant="outlined"
            disabled={running || !libraryId}
            onClick={() =>
              canPickFolder()
                ? void pickCard().then(opened)
                : input.current?.click()
            }
          >
            Choose the card
          </Button>
        </div>
        {card && !finished && (
          <>
            <Typography>
              <strong>{card.name}</strong>: {card.files.length} files. Choose
              the folders to import.
            </Typography>
            <Stack>
              {tops.map(([name, files]) => (
                <FormControlLabel
                  key={name}
                  control={
                    <Checkbox
                      checked={chosen.has(name)}
                      disabled={running}
                      onChange={(e) => {
                        const next = new Set(chosen);
                        if (e.target.checked) next.add(name);
                        else next.delete(name);
                        setChosen(next);
                      }}
                    />
                  }
                  label={`${name || "Files at the top"}: ${files.length} files, ${size(files.reduce((n, f) => n + f.file.size, 0))}`}
                />
              ))}
            </Stack>
            <TextField
              label="Unit"
              helperText="The unit that recorded these, e.g. the label on the card (VAN7)"
              value={unit}
              disabled={running}
              onChange={(e) => setUnit(e.target.value)}
              sx={{ maxWidth: 360 }}
            />
            <div>
              <Button
                variant="contained"
                disabled={running || count === 0}
                onClick={() => void run()}
              >
                Import {count} file{count === 1 ? "" : "s"}
              </Button>
            </div>
          </>
        )}
        {running && progress && (
          <Stack spacing={1} aria-label="Card import progress">
            <Typography>
              {progress.done} of {progress.total}: {progress.file}. Keep this
              page open.
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                progress.allBytes
                  ? (100 * progress.bytes) / progress.allBytes
                  : 0
              }
            />
          </Stack>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {finished && (
          <Alert
            severity={
              finished.failed.length || finished.unmatched.length
                ? "warning"
                : "success"
            }
          >
            Import from {card?.name} finished: {finished.counts.catalogued}{" "}
            imported, {finished.counts.needs_review} need review,{" "}
            {finished.counts.duplicates} already held,{" "}
            {finished.counts.rejected} rejected.
            {finished.failed.length + finished.unmatched.length === 0
              ? " Every file arrived exactly as read from the card."
              : ` Not verified: ${[...finished.failed, ...finished.unmatched].join("; ")}.`}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
